import React, { useState } from 'react';
import '../styles/Common.css';
import { useTranslation } from "react-i18next";
import SEO from './SEO';
import heroBackgroundImage from '../assets/images/hero_background/about_hero_bg_original4.jpg';

const Clawaudit = () => {
    const { t, i18n } = useTranslation(['home', 'common', 'clawaudit'])
    const isChinese = i18n.language.includes('zh');
    const features = t('features', { ns: 'clawaudit', returnObjects: true });
    const scanPointsRows = t('scan_points_table.rows', { ns: 'clawaudit', returnObjects: true });
    const applicableScenarios = t('applicable_scenarios', { ns: 'clawaudit', returnObjects: true });
    const usageMethods = t('usage_methods', { ns: 'clawaudit', returnObjects: true });
    const [zoomedImage, setZoomedImage] = useState(null);

    const handleImageClick = (src) => {
        setZoomedImage(src);
    };

    const closeZoom = () => {
        setZoomedImage(null);
    };

    return (
        <div className="bg-white">
            <SEO title={t('html_title', { ns: 'clawaudit' })}></SEO>

            {/* --- Hero Section (same as About.js - centered) --- */}
            <div 
                className="bg-cover bg-white bg-blend-hard-light bg-bottom bg-no-repeat min-h-[350px] sm:min-h-[400px] w-full flex flex-col justify-start items-center p-6"
                style={{backgroundImage: `url(${heroBackgroundImage})`}}
            >
                <div className="flex flex-col justify-center items-center max-w-[950px] mt-36 mb-16">
                    <div className={`font-bold tracking-[.02em] text-white/90 leading-normal text-center ${isChinese ? 'text-4xl sm:text-[3.5rem] sm:tracking-[.05em]' : 'text-4xl sm:text-[3rem] sm:tracking-[.03em]'}`}>
                        {t('hero_title', { ns: 'clawaudit' })}
                    </div>
                    <div className={`mt-3 text-white/80 text-lg sm:text-xl font-normal tracking-[.02em] leading-relaxed text-center ${isChinese ? 'tracking-[.05em]' : 'tracking-[.01em]'}`}>
                        {t('hero_subtitle', { ns: 'clawaudit' })}
                    </div>
                </div>
            </div>

            {/* --- Content Section (same as About.js - logo on left, content on right) --- */}
            <div className="content-container py-24">
                <div className="flex flex-wrap justify-evenly items-start max-w-8xl mx-auto px-6 sm:px-12 pb-36 rounded-3xl bg-cover bg-white bg-blend-multiply bg-center-top bg-no-repeat">
                    {/* Logo on left */}
                    <div className="flex justify-center items-center flex-col pt-0 pb-6 bg-white">
                        <img 
                            src="/logo/clawaudit/logo.png" 
                            alt="Foresight ClawAudit" 
                            className="w-full max-w-[400px] bg-white rounded-lg p-4"
                        />
                    </div>
                    
                    {/* Content on right */}
                    <div className="lg:max-w-[50%] w-full overflow-hidden">
                        {/* --- Overview Section (Bento Box) --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_overview_title', { ns: 'clawaudit' })}
                            </p>
                            <div className="text-neutral-600 text-xl font-light font-sans leading-loose">
                                <p className="max-w-[900px]">{t('section_overview_content', { ns: 'clawaudit' })}</p>
                                <p className="max-w-[900px] mt-4">{t('section_overview_content_2', { ns: 'clawaudit' })}</p>
                            </div>
                        </section>

                        {/* --- Features Section --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_features_title', { ns: 'clawaudit' })}
                            </p>
                            <div className="grid grid-cols-1 gap-6">
                                {Array.isArray(features) && features.map((feature, index) => (
                                    <div key={index} className="bg-gray-50 rounded-xl p-6">
                                        <div className="text-3xl mb-4">
                                            {index === 0 ? '🛡️' : index === 1 ? '📊' : '🔍'}
                                        </div>
                                        <div className="text-neutral-600 text-xl font-light font-sans leading-loose">{feature}</div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* --- Scan Points Table Section --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_scan_points_title', { ns: 'clawaudit' })}
                            </p>

                            <div className="bg-gray-50 rounded-2xl overflow-hidden">
                                <div className="overflow-x-auto min-w-0">
                                    <table className="w-full border-collapse text-sm sm:text-base min-w-[600px]">
                                        <thead>
                                            <tr className="bg-[#c00000] text-white">
                                                <th className="p-4 text-left font-semibold">{t('scan_points_table.category', { ns: 'clawaudit' })}</th>
                                                <th className="p-4 text-center font-semibold w-20">{t('scan_points_table.count', { ns: 'clawaudit' })}</th>
                                                <th className="p-4 text-left font-semibold">{t('scan_points_table.description', { ns: 'clawaudit' })}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(scanPointsRows) && scanPointsRows.map((row, index) => (
                                                <tr key={index} className={index % 2 === 0 ? 'bg-transparent' : 'bg-gray-100/50'}>
                                                    <td className="p-4 font-semibold text-gray-800">{row.category}</td>
                                                    <td className="p-4 text-center text-gray-700">{row.count}</td>
                                                    <td className="p-4 text-gray-600">{row.description}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>

                        {/* --- Applicable Scenarios Section --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_applicable_title', { ns: 'clawaudit' })}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Array.isArray(applicableScenarios) && applicableScenarios.map((scenario, index) => (
                                    <div key={index} className="bg-gray-50 rounded-xl p-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">{scenario.title}</h3>
                                        <p className="text-neutral-600 text-xl font-light font-sans leading-loose">{scenario.description}</p>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* --- Quick Start Guide Section --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_usage_title', { ns: 'clawaudit' })}
                            </p>

                            <div className="space-y-6">
                                {Array.isArray(usageMethods) && usageMethods.map((method, index) => (
                                    <div key={index} className="bg-gray-50 rounded-xl p-6">
                                        <div className="w-full">
                                            <h3 className="text-xl font-semibold text-[#c00000] mb-4">{method.title}</h3>
                                            {method.code && (
                                                <pre className="bg-[#1e1e1e] text-[#d4d4d4] p-4 rounded-lg overflow-x-auto font-mono text-sm mb-3 whitespace-pre-wrap break-all"> 
                                                    {method.code}
                                                </pre>
                                            )}
                                            {method.note && (
                                                <p className="text-neutral-600 text-xl font-light font-sans leading-loose mb-3">{method.note}</p>
                                            )}
                                            {method.commands && Array.isArray(method.commands) && (
                                                <pre className="bg-[#1e1e1e] text-[#d4d4d4] p-4 rounded-lg overflow-x-auto font-mono text-sm whitespace-pre-wrap break-all"> 
                                                    {method.commands.join('\n')}
                                                </pre>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8">
                                <p className={`max-w-[900px] mb-4 text-lg font-medium font-sans text-[#c00000] leading-loose ${isChinese ? 'tracking-[.05em]' : 'tracking-[.01em]'}`}>
                                    {t('section_output_example_title', { ns: 'clawaudit' })}
                                </p>
                                <img 
                                    src="/images/clawaudit/example1.webp" 
                                    alt="ClawAudit Output Example" 
                                    className="w-full rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
                                    onClick={() => handleImageClick('/images/clawaudit/example1.webp')}
                                />
                            </div>
                        </section>

                        {/* --- Quote Section --- */}
                        <section className="mb-16">
                            <p className={`max-w-[900px] mt-6 mb-3 text-xl font-medium font-sans text-[#c00000] sm:text-[#c00000] sm:text-xl leading-loose ${isChinese ? 'tracking-[.05em] sm:tracking-[.01em]' : 'text-left tracking-[.01em] sm:tracking-[.01em]'}`}>
                                {t('section_quote_title', { ns: 'clawaudit' })}
                            </p>

                            <div className="text-neutral-600 text-xl font-light font-sans leading-loose">
                                <p className="italic">"{t('quote', { ns: 'clawaudit' })}"</p>
                                <p className="mt-4 font-medium text-[#c00000] text-right">— {t('quote_author', { ns: 'clawaudit' })}</p>
                            </div>
                        </section>

                        {/* --- Links Section --- */}
                        <section>
                            <div className="flex flex-wrap justify-start gap-6">
                                <a 
                                    href="https://pypi.org/project/clawaudit/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="w-full bg-[#c00000] hover:bg-[#a00000] text-white font-semibold text-lg py-4 px-6 rounded-xl transition-colors duration-300 text-center"
                                >
                                    {t('cta_button_clawaudit', { ns: 'clawaudit' })}
                                </a>
                                {/* <a 
                                    href="https://github.com/Beijing-AISI/Clawaudit" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-xl p-6 transition-colors duration-300"
                                >
                                    <span className="text-[#c00000]">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                        </svg>
                                    </span>
                                    <span className="text-gray-800 font-semibold text-lg">GitHub</span>
                                </a>
                                <a 
                                    href="https://beijing-aisi.ac.cn/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-xl p-6 transition-colors duration-300"
                                >
                                    <span className="text-[#c00000]">
                                        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                                        </svg>
                                    </span>
                                    <span className="text-gray-800 font-semibold text-lg">Beijing-AISI</span>
                                </a> */}
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {zoomedImage && (
                <div 
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 cursor-pointer"
                    onClick={closeZoom}
                >
                    <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                        <button 
                            className="absolute -top-10 right-0 text-white text-4xl hover:text-gray-300 transition-colors"
                            onClick={closeZoom}
                        >
                            ×
                        </button>
                        <img 
                            src={zoomedImage} 
                            alt="Zoomed" 
                            className="max-w-full max-h-[90vh] object-contain" 
                        />
                    </div>
                </div>
            )}

        </div>
    )
}

export default Clawaudit